import { Add01Icon, Delete02Icon, SentIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { CreateVersionDialog } from "@/components/case/create-version-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import {
  api,
  ApiError,
  formatOpapiFailure,
  formatPublishFailureDetail,
  type CaseDetail,
  type CaseVersion,
} from "@/lib/api";
import {
  formatVersionStatus,
  formatWhen,
  statusBadgeVariant,
} from "@/lib/case-format";
import {
  productionAudienceLabel,
  publishedLocalesFromStatus,
  resolvePublishVersionIds,
} from "@/lib/publish-targets";
import { cn } from "@/lib/utils";
import { publishedVersionIds } from "@/hooks/use-case-detail";

function toggleLocaleList(
  list: string[],
  locale: string,
  checked: boolean,
): string[] {
  if (checked) {
    if (list.includes(locale)) return list;
    return [...list, locale].sort();
  }
  return list.filter((loc) => loc !== locale);
}

export function VersionsTable({
  caseId,
  detail,
}: {
  caseId: string;
  detail: CaseDetail;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const versions = detail.versions;
  const versionById = useMemo(
    () => new Map(versions.map((v) => [v.id, v])),
    [versions],
  );
  const productionIds = publishedVersionIds(detail);
  const [forcePublish, setForcePublish] = useState(false);
  const [publishAsLatest, setPublishAsLatest] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [publishTarget, setPublishTarget] = useState<CaseVersion | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<CaseVersion | null>(
    null,
  );
  const [selectedPublishLocales, setSelectedPublishLocales] = useState<
    string[]
  >([]);
  const [selectedUnpublishLocales, setSelectedUnpublishLocales] = useState<
    string[]
  >([]);
  const [deleteTarget, setDeleteTarget] = useState<CaseVersion | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publishQuery = useQuery({
    queryKey: ["case-publish", caseId],
    queryFn: () => api.getCasePublish(caseId),
  });

  const groupsQuery = useQuery({
    queryKey: ["user-groups"],
    queryFn: () => api.listUserGroups(),
  });

  const userGroups = groupsQuery.data?.groups ?? [];

  const selectedGroupUidCount = useMemo(() => {
    if (selectedGroupIds.length === 0) return 0;
    const set = new Set<number>();
    for (const group of userGroups) {
      if (!selectedGroupIds.includes(group.id)) continue;
      for (const uid of group.uids) set.add(uid);
    }
    return set.size;
  }, [selectedGroupIds, userGroups]);

  const localesWithPhone = useMemo(() => {
    return versions
      .filter((v) => v.phoneJsonR2Key)
      .map((v) => v.locale)
      .filter((loc, i, arr) => arr.indexOf(loc) === i)
      .sort();
  }, [versions]);

  const liveLocales = useMemo(
    () => publishedLocalesFromStatus(publishQuery.data?.locales ?? []),
    [publishQuery.data?.locales],
  );

  const localeMetaByCode = useMemo(() => {
    const map = new Map(
      (publishQuery.data?.locales ?? []).map((loc) => [loc.locale, loc]),
    );
    return map;
  }, [publishQuery.data?.locales]);

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["case", caseId] }),
      queryClient.invalidateQueries({ queryKey: ["case-publish", caseId] }),
      queryClient.invalidateQueries({ queryKey: ["case-preview", caseId] }),
      queryClient.invalidateQueries({ queryKey: ["cases"] }),
    ]);
  };

  const openPublishFor = (version: CaseVersion) => {
    setPublishTarget(version);
    setSelectedPublishLocales(
      localesWithPhone.includes(version.locale)
        ? [version.locale]
        : [...localesWithPhone],
    );
    setForcePublish(false);
    setPublishAsLatest(false);
    setSelectedGroupIds([]);
  };

  const openUnpublishFor = (version: CaseVersion) => {
    setUnpublishTarget(version);
    const liveCodes = liveLocales.map((l) => l.locale);
    setSelectedUnpublishLocales(
      liveCodes.includes(version.locale) ? [version.locale] : [...liveCodes],
    );
  };

  const publishMutation = useMutation({
    mutationFn: (preferred: CaseVersion) => {
      const versionIds = resolvePublishVersionIds(
        selectedPublishLocales,
        versions,
        preferred,
      );
      if (versionIds.length === 0) {
        return Promise.reject(
          new ApiError(400, "no_publish_targets", "没有可发布的包"),
        );
      }
      return api.publishCase(caseId, {
        versionIds,
        force: forcePublish || undefined,
        publishAsLatest: publishAsLatest || undefined,
        groupIds: selectedGroupIds,
      });
    },
    onSuccess: async (result) => {
      setError(null);
      setPublishTarget(null);
      setForcePublish(false);
      setPublishAsLatest(false);
      setSelectedGroupIds([]);
      const audienceNote =
        result.uids && result.uids.length > 0
          ? `定向 ${result.uids.length} 个 UID。`
          : "全量发布。";
      toast.add({
        type: result.ok ? "success" : "error",
        title: result.ok ? "已发布" : "发布失败",
        description: result.ok
          ? `已通过 opapi 发布 ${result.published.length} 个语言。${audienceNote}`
          : formatOpapiFailure(result.opapi),
        priority: result.ok ? undefined : "high",
      });
      await invalidateAll();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "publish_failed";
      const toastDetail = formatPublishFailureDetail(
        code,
        err instanceof ApiError ? err.note : undefined,
      );
      setPublishTarget(null);
      setForcePublish(false);
      setPublishAsLatest(false);
      setError(toastDetail);
      toast.add({
        type: "error",
        title:
          code === "publish_gate_failed"
            ? "发布检查未通过"
            : "发布失败",
        description: toastDetail,
        priority: "high",
      });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: () => {
      const versionIds = selectedUnpublishLocales
        .map(
          (locale) =>
            liveLocales.find((l) => l.locale === locale)?.versionId,
        )
        .filter((id): id is string => Boolean(id));
      if (versionIds.length === 0) {
        return Promise.reject(
          new ApiError(400, "version_id_required", "未选择语言"),
        );
      }
      return api.unpublishCase(caseId, { versionIds });
    },
    onSuccess: async (result) => {
      setError(null);
      setUnpublishTarget(null);
      const locales = result.unpublished.map((u) => u.locale).join(", ");
      toast.add({
        type: "success",
        title: "已取消发布",
        description: `已从生产环境与 OPAPI 移除 ${result.unpublished.length} 个语言：${locales}。`,
      });
      await invalidateAll();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "unpublish_failed";
      const detail =
        err instanceof ApiError && err.note ? `${code}: ${err.note}` : code;
      setUnpublishTarget(null);
      setError(detail);
      toast.add({
        type: "error",
        title: "取消发布失败",
        description: detail,
        priority: "high",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (version: CaseVersion) => api.deleteVersion(version.id),
    onSuccess: async (_result, version) => {
      setError(null);
      setDeleteTarget(null);
      toast.add({
        type: "success",
        title: "版本已删除",
        description: `已移除 ${version.locale} v${version.versionN}。`,
      });
      await invalidateAll();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "delete_failed";
      const detail =
        err instanceof ApiError && err.note ? `${code}: ${err.note}` : code;
      setDeleteTarget(null);
      setError(detail);
      toast.add({
        type: "error",
        title: "删除失败",
        description: detail,
        priority: "high",
      });
    },
  });

  const busy =
    publishMutation.isPending ||
    unpublishMutation.isPending ||
    deleteMutation.isPending;

  const openVersion = (versionId: string) => {
    const version = versionById.get(versionId);
    const tab =
      version?.status === "draft" && !version.hasPipeline ? "meta" : "pipeline";
    void navigate({
      to: `/cases/$caseId/versions/$versionId/${tab}`,
      params: { caseId, versionId },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>版本</CardTitle>
          <CardDescription>
            此案件的所有草稿与已发布包。草稿行打开元信息审核；已启动的版本打开流水线。发布与取消发布对话框可多选语言。「创建版本」会从源版本派生新版本，可填备注方便识别。
          </CardDescription>
          <CardAction>
            <Button
              disabled={versions.length === 0}
              onClick={() => setCreateOpen(true)}
            >
              <HugeiconsIcon icon={Add01Icon} data-icon="inline-start" />
              创建版本
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>操作失败</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {versions.length === 0 ? (
            <Empty className="border border-border py-8">
              <EmptyHeader>
                <EmptyTitle>暂无版本</EmptyTitle>
                <EmptyDescription>
                  创建案件时会自动生成一个草稿版本。
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>语言</TableHead>
                    <TableHead>版本</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>生产</TableHead>
                    <TableHead>备注</TableHead>
                    <TableHead>父版本</TableHead>
                    <TableHead>Phone pin</TableHead>
                    <TableHead>更新时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((v) => {
                    const parent = v.parentVersionId
                      ? versionById.get(v.parentVersionId)
                      : null;
                    const isProduction = productionIds.has(v.id);
                    const publishMeta = localeMetaByCode.get(v.locale);
                    const audienceGroups =
                      publishMeta?.publishGroups &&
                      publishMeta.publishGroups.length > 0
                        ? publishMeta.publishGroups
                        : userGroups;
                    const productionLabel = isProduction
                      ? productionAudienceLabel(
                          publishMeta?.groupIds,
                          audienceGroups,
                        )
                      : null;
                    const canPublish = Boolean(v.phoneJsonR2Key);
                    const canDelete = !isProduction && v.status !== "published";
                    return (
                      <TableRow
                        key={v.id}
                        className="cursor-pointer"
                        onClick={() => openVersion(v.id)}
                      >
                        <TableCell className="font-medium">{v.locale}</TableCell>
                        <TableCell>v{v.versionN}</TableCell>
                        <TableCell>
                          <Badge variant={statusBadgeVariant(v.status)}>
                            {formatVersionStatus(v.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isProduction ? (
                            <Badge
                              variant={productionLabel ? "warning" : "success"}
                              className="max-w-[10rem] truncate"
                              title={productionLabel ?? "全量"}
                            >
                              {productionLabel ?? "全量"}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[8rem] truncate text-xs text-muted-foreground">
                          {v.branchLabel ?? "-"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {parent
                            ? `v${parent.versionN}`
                            : v.parentVersionId
                              ? `${v.parentVersionId.slice(0, 8)}…`
                              : "-"}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {v.phoneRuntimeVersion}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatWhen(v.updatedAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div
                            className="flex flex-wrap items-center justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              disabled={busy}
                              onClick={() => openVersion(v.id)}
                            >
                              打开
                            </Button>
                            {isProduction ? (
                              <AlertDialog
                                open={unpublishTarget?.id === v.id}
                                onOpenChange={(open) => {
                                  if (unpublishMutation.isPending) return;
                                  if (!open) setUnpublishTarget(null);
                                }}
                              >
                                <AlertDialogTrigger
                                  render={
                                    <Button
                                      variant="outline"
                                      disabled={
                                        busy ||
                                        !publishQuery.data?.opapiConfigured
                                      }
                                      onClick={() => openUnpublishFor(v)}
                                    />
                                  }
                                >
                                  {unpublishMutation.isPending &&
                                  unpublishTarget?.id === v.id ? (
                                    <Spinner />
                                  ) : null}
                                  取消发布
                                </AlertDialogTrigger>
                                <AlertDialogContent className="max-w-md sm:max-w-md">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      从生产环境取消发布？
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      选择要从生产环境移除的一个或多个线上语言。每个选中语言会从
                                      OPAPI 删除。版本回到草稿，之后可再次发布。
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <Field>
                                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                      <FieldLabel>语言</FieldLabel>
                                      <div className="flex gap-2">
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          disabled={
                                            unpublishMutation.isPending
                                          }
                                          onClick={() =>
                                            setSelectedUnpublishLocales(
                                              liveLocales.map((l) => l.locale),
                                            )
                                          }
                                        >
                                          全选
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          disabled={
                                            unpublishMutation.isPending
                                          }
                                          onClick={() =>
                                            setSelectedUnpublishLocales([])
                                          }
                                        >
                                          清空
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-2">
                                      {liveLocales.map((live) => {
                                        const checked =
                                          selectedUnpublishLocales.includes(
                                            live.locale,
                                          );
                                        const liveVersion = versionById.get(
                                          live.versionId,
                                        );
                                        return (
                                          <label
                                            key={live.locale}
                                            className={cn(
                                              "flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm",
                                              "hover:bg-muted/60",
                                              checked
                                                ? "bg-muted/40"
                                                : undefined,
                                            )}
                                          >
                                            <input
                                              type="checkbox"
                                              className="size-4 shrink-0 rounded border border-input accent-primary"
                                              checked={checked}
                                              disabled={
                                                unpublishMutation.isPending
                                              }
                                              onChange={(e) =>
                                                setSelectedUnpublishLocales(
                                                  (prev) =>
                                                    toggleLocaleList(
                                                      prev,
                                                      live.locale,
                                                      e.target.checked,
                                                    ),
                                                )
                                              }
                                            />
                                            <span className="min-w-10 font-medium">
                                              {live.locale}
                                            </span>
                                            {liveVersion ? (
                                              <span className="text-xs text-muted-foreground">
                                                v{liveVersion.versionN}
                                              </span>
                                            ) : null}
                                            <Badge
                                              variant="success"
                                              className="ml-auto"
                                            >
                                              线上
                                            </Badge>
                                          </label>
                                        );
                                      })}
                                    </div>
                                    <FieldDescription>
                                      {selectedUnpublishLocales.length === 0
                                        ? "请至少选择一种语言以取消发布。"
                                        : `将取消发布：${selectedUnpublishLocales.join(", ")}。`}
                                    </FieldDescription>
                                  </Field>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel
                                      disabled={unpublishMutation.isPending}
                                    >
                                      取消
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      variant="destructive"
                                      disabled={
                                        unpublishMutation.isPending ||
                                        selectedUnpublishLocales.length === 0
                                      }
                                      onClick={() =>
                                        unpublishMutation.mutate()
                                      }
                                    >
                                      {unpublishMutation.isPending ? (
                                        <Spinner />
                                      ) : null}
                                      取消发布
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            ) : (
                              <AlertDialog
                                open={publishTarget?.id === v.id}
                                onOpenChange={(open) => {
                                  if (publishMutation.isPending) return;
                                  if (!open) {
                                    setPublishTarget(null);
                                    setForcePublish(false);
                                    setPublishAsLatest(false);
                                  }
                                }}
                              >
                                <AlertDialogTrigger
                                  render={
                                    <Button
                                      variant="outline"
                                      disabled={
                                        busy ||
                                        !canPublish ||
                                        !publishQuery.data?.opapiConfigured
                                      }
                                      onClick={() => openPublishFor(v)}
                                    />
                                  }
                                >
                                  {publishMutation.isPending &&
                                  publishTarget?.id === v.id ? (
                                    <Spinner />
                                  ) : (
                                    <HugeiconsIcon
                                      icon={SentIcon}
                                      data-icon="inline-start"
                                    />
                                  )}
                                  发布
                                </AlertDialogTrigger>
                                <AlertDialogContent className="max-w-md sm:max-w-md">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      发布到生产环境？
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      选择一种或多种语言。此行版本优先用于{" "}
                                      {v.locale}
                                      ；其他语言使用各自最高手机包。每个语言的先前生产版本会被取代。
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="flex flex-col gap-4">
                                    <Field>
                                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                        <FieldLabel>语言</FieldLabel>
                                        <div className="flex gap-2">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            disabled={
                                              publishMutation.isPending
                                            }
                                            onClick={() =>
                                              setSelectedPublishLocales([
                                                ...localesWithPhone,
                                              ])
                                            }
                                          >
                                            全选
                                          </Button>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            disabled={
                                              publishMutation.isPending
                                            }
                                            onClick={() =>
                                              setSelectedPublishLocales([])
                                            }
                                          >
                                            清空
                                          </Button>
                                        </div>
                                      </div>
                                      <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-2">
                                        {localesWithPhone.map((loc) => {
                                          const meta =
                                            localeMetaByCode.get(loc);
                                          const checked =
                                            selectedPublishLocales.includes(
                                              loc,
                                            );
                                          return (
                                            <label
                                              key={loc}
                                              className={cn(
                                                "flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm",
                                                "hover:bg-muted/60",
                                                checked
                                                  ? "bg-muted/40"
                                                  : undefined,
                                              )}
                                            >
                                              <input
                                                type="checkbox"
                                                className="size-4 shrink-0 rounded border border-input accent-primary"
                                                checked={checked}
                                                disabled={
                                                  publishMutation.isPending
                                                }
                                                onChange={(e) =>
                                                  setSelectedPublishLocales(
                                                    (prev) =>
                                                      toggleLocaleList(
                                                        prev,
                                                        loc,
                                                        e.target.checked,
                                                      ),
                                                  )
                                                }
                                              />
                                              <span className="min-w-10 font-medium">
                                                {loc}
                                              </span>
                                              {loc === v.locale ? (
                                                <span className="text-xs text-muted-foreground">
                                                  v{v.versionN}（此行）
                                                </span>
                                              ) : meta?.latestVersionN !=
                                                null ? (
                                                <span className="text-xs text-muted-foreground">
                                                  v{meta.latestVersionN}
                                                  {meta.latestStatus
                                                    ? ` · ${formatVersionStatus(meta.latestStatus)}`
                                                    : ""}
                                                </span>
                                              ) : null}
                                              {meta?.publishedVersionId ? (
                                                <Badge
                                                  variant="success"
                                                  className="ml-auto"
                                                >
                                                  线上
                                                </Badge>
                                              ) : (
                                                <span className="ml-auto text-xs text-muted-foreground">
                                                  未上线
                                                </span>
                                              )}
                                            </label>
                                          );
                                        })}
                                      </div>
                                      <FieldDescription>
                                        {selectedPublishLocales.length === 0
                                          ? "请至少选择一种语言以发布。"
                                          : `将发布：${selectedPublishLocales.join(", ")}。`}
                                      </FieldDescription>
                                    </Field>
                                    <Field>
                                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                        <FieldLabel>发布范围（用户组）</FieldLabel>
                                        <div className="flex gap-2">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            disabled={
                                              publishMutation.isPending ||
                                              userGroups.length === 0
                                            }
                                            onClick={() =>
                                              setSelectedGroupIds(
                                                userGroups.map((g) => g.id),
                                              )
                                            }
                                          >
                                            全选
                                          </Button>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            disabled={publishMutation.isPending}
                                            onClick={() =>
                                              setSelectedGroupIds([])
                                            }
                                          >
                                            清空
                                          </Button>
                                        </div>
                                      </div>
                                      {userGroups.length === 0 ? (
                                        <p className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
                                          暂无用户组。不选即全量发布。
                                        </p>
                                      ) : (
                                        <div className="flex max-h-36 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-2">
                                          {userGroups.map((group) => {
                                            const checked =
                                              selectedGroupIds.includes(
                                                group.id,
                                              );
                                            return (
                                              <label
                                                key={group.id}
                                                className={cn(
                                                  "flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm",
                                                  "hover:bg-muted/60",
                                                  checked
                                                    ? "bg-muted/40"
                                                    : undefined,
                                                )}
                                              >
                                                <input
                                                  type="checkbox"
                                                  className="size-4 shrink-0 rounded border border-input accent-primary"
                                                  checked={checked}
                                                  disabled={
                                                    publishMutation.isPending
                                                  }
                                                  onChange={(e) =>
                                                    setSelectedGroupIds(
                                                      (prev) =>
                                                        e.target.checked
                                                          ? prev.includes(
                                                              group.id,
                                                            )
                                                            ? prev
                                                            : [
                                                                ...prev,
                                                                group.id,
                                                              ]
                                                          : prev.filter(
                                                              (id) =>
                                                                id !==
                                                                group.id,
                                                            ),
                                                    )
                                                  }
                                                />
                                                <span className="font-medium">
                                                  {group.name}
                                                </span>
                                                <span className="ml-auto text-xs text-muted-foreground">
                                                  {group.uidCount} UID
                                                </span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      )}
                                      <FieldDescription>
                                        {selectedGroupIds.length === 0
                                          ? "全量发布（不限制 UID）。"
                                          : `定向发布：合并 ${selectedGroupIds.length} 个用户组，共 ${selectedGroupUidCount} 个 UID。`}
                                      </FieldDescription>
                                    </Field>
                                    <div className="flex flex-col gap-3">
                                      <Field
                                        orientation="horizontal"
                                        className="w-fit"
                                      >
                                        <Switch
                                          id={`versions-force-publish-${v.id}`}
                                          checked={forcePublish}
                                          disabled={publishMutation.isPending}
                                          onCheckedChange={setForcePublish}
                                        />
                                        <FieldLabel
                                          htmlFor={`versions-force-publish-${v.id}`}
                                        >
                                          强制发布
                                        </FieldLabel>
                                      </Field>
                                      <p className="text-xs text-muted-foreground">
                                        跳过就绪检查。不完整的包也可能上线。
                                      </p>
                                      <Field
                                        orientation="horizontal"
                                        className="w-fit"
                                      >
                                        <Switch
                                          id={`versions-publish-latest-${v.id}`}
                                          checked={publishAsLatest}
                                          disabled={publishMutation.isPending}
                                          onCheckedChange={setPublishAsLatest}
                                        />
                                        <FieldLabel
                                          htmlFor={`versions-publish-latest-${v.id}`}
                                        >
                                          发布为 latest
                                        </FieldLabel>
                                      </Field>
                                      <p className="text-xs text-muted-foreground">
                                        仅当版本 pin 为浮动 latest
                                        时可用。已发布 JSON 将携带字面 pin
                                        latest。
                                      </p>
                                    </div>
                                  </div>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel
                                      disabled={publishMutation.isPending}
                                    >
                                      取消
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      disabled={
                                        publishMutation.isPending ||
                                        selectedPublishLocales.length === 0
                                      }
                                      onClick={() =>
                                        publishMutation.mutate(v)
                                      }
                                    >
                                      {publishMutation.isPending ? (
                                        <Spinner />
                                      ) : null}
                                      {forcePublish
                                        ? "强制发布为生产"
                                        : "发布为生产"}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                            <AlertDialog
                              open={deleteTarget?.id === v.id}
                              onOpenChange={(open) => {
                                if (deleteMutation.isPending) return;
                                if (!open) setDeleteTarget(null);
                              }}
                            >
                              <AlertDialogTrigger
                                render={
                                  <Button
                                    variant="outline"
                                    disabled={busy || !canDelete}
                                    title={
                                      canDelete
                                        ? "删除版本"
                                        : "生产版本不可删除"
                                    }
                                    onClick={() => setDeleteTarget(v)}
                                  />
                                }
                              >
                                {deleteMutation.isPending &&
                                deleteTarget?.id === v.id ? (
                                  <Spinner />
                                ) : (
                                  <HugeiconsIcon
                                    icon={Delete02Icon}
                                    data-icon="inline-start"
                                  />
                                )}
                                删除
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    删除 {v.locale} v{v.versionN}？
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    将永久移除此版本及其流水线运行、素材和 R2
                                    包文件。子派生版本保留数据但失去父链接。此操作不可撤销。
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel
                                    disabled={deleteMutation.isPending}
                                  >
                                    取消
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    variant="destructive"
                                    disabled={deleteMutation.isPending}
                                    onClick={() => deleteMutation.mutate(v)}
                                  >
                                    {deleteMutation.isPending ? (
                                      <Spinner />
                                    ) : null}
                                    删除版本
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateVersionDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        caseId={caseId}
        detail={detail}
      />
    </div>
  );
}
